import { filter } from "lodash";
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
// material
import {
    Card,
    Table,
    Stack,
    Checkbox,
    TableRow,
    TableBody,
    TableCell,
    Typography,
    TableContainer,
    TablePagination,
    LinearProgress,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    IconButton,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
// components
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "./ListHead";
import SearchList from "src/components/SearchList";
import Box from "@mui/material/Box";
import { useTranslation } from 'react-i18next';
//

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, headers, comparator, query) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    if (query) {
        return filter(array, (_el) =>
            headers.some(
                (header) =>
                    header.search &&
                    getSearchableText(_el[header.id])
                        .toLowerCase()
                        .indexOf(query.toLowerCase()) !== -1
            )
        );
    }
    return stabilizedThis.map((el) => el[0]);
}

function getSearchableText(value) {
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number") {
        return String(value);
    }
    return "";
}

function applyFilterOnly(array, headers, query) {
    if (!query) {
        return array;
    }
    return filter(array, (_el) =>
        headers.some(
            (header) =>
                header.search &&
                getSearchableText(_el[header.id])
                    .toLowerCase()
                    .indexOf(query.toLowerCase()) !== -1
        )
    );
}

const SortableTableRow = ({
    row,
    isItemSelected,
    renderRow,
    onSelectionChange,
    onMouseEnter,
    dragId,
    dragDisabled,
    dragAriaLabel,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: dragId,
        disabled: dragDisabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
    };

    return (
        <TableRow
            ref={setNodeRef}
            hover
            key={row.index}
            tabIndex={-1}
            role="checkbox"
            selected={isItemSelected}
            aria-checked={isItemSelected}
            onMouseEnter={onMouseEnter}
            style={style}
        >
            <TableCell padding="checkbox">
                <Checkbox
                    checked={isItemSelected}
                    onChange={onSelectionChange}
                />
            </TableCell>
            <TableCell padding="checkbox">
                <IconButton
                    size="small"
                    aria-label={dragAriaLabel}
                    sx={{ cursor: dragDisabled ? "not-allowed" : "grab" }}
                    {...(dragDisabled ? {} : attributes)}
                    {...(dragDisabled ? {} : listeners)}
                >
                    <DragIndicatorIcon fontSize="small" />
                </IconButton>
            </TableCell>
            {renderRow(row)}
        </TableRow>
    );
};

const ROWS_PER_PAGE = [10, 20, 30];

const AdminTable = ({
    children,
    showChildrenWhenSelected,
    headers,
    rows,
    searchPlaceholder,
    selection,
    progress,
    contextMenuOptions,
    onSelectionChanged,
    rowDrag,
}) => {
    const [page, setPage] = useState(0);
    const [order, setOrder] = useState("asc");
    const { selected, setSelected } = selection;
    const [orderBy, setOrderBy] = useState("name");
    const [filterName, setFilterName] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE[0]);
    const [contextMenu, setContextMenu] = useState(null);
    const [currentHoverId, setCurrentHoverId] = useState(null);
    const items = rows.map((row, index) => {
        const derivedDragId =
            typeof rowDrag?.getRowId === "function"
                ? rowDrag.getRowId(row, index)
                : rowDrag?.idField && row[rowDrag.idField] != null
                  ? row[rowDrag.idField]
                  : index;
        return {
            ...row,
            index: index,
            __dragId: derivedDragId != null ? String(derivedDragId) : String(index),
        };
    });
    const { t } = useTranslation();
    const dragEnabled =
        Boolean(rowDrag?.enabled) &&
        typeof rowDrag?.onReorder === "function" &&
        items.length > 1;
    const disableDragWhenFiltered = rowDrag?.disableWhenFiltered !== false;
    const isDragDisabled = !dragEnabled || (disableDragWhenFiltered && !!filterName);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 6,
            },
        })
    );
    const effectiveHeaders = dragEnabled
        ? [{ id: "__drag", label: "", alignRight: false, search: false }, ...headers]
        : headers;
    const showPagination = !dragEnabled;
    const tableColumnSpan = headers.length + 1;
    const dragTableColumnSpan = headers.length + 2;

    const handleContextMenu = (event) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                      mouseX: event.clientX - 2,
                      mouseY: event.clientY - 4,
                  }
                : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                  // Other native context menus might behave different.
                  // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                  null
        );
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handleRequestSort = (event, property) => {
        if (dragEnabled || property === "__drag") {
            return;
        }
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const id = headers[0].id;
            const newSelecteds = items.map((n) => n[id]);
            if (onSelectionChanged) {
                onSelectionChanged(selected, newSelecteds);
            }

            setSelected(newSelecteds);
            return;
        }
        if (onSelectionChanged) {
            onSelectionChanged(selected, []);
        }

        setSelected([]);
    };

    const handleClick = (event, name) => {
        if (event.target.checked) {
            if (onSelectionChanged) {
                onSelectionChanged(selected, [...selected, name]);
            }

            setSelected([...selected, name]);
        } else {
            let tmpSelected = [...selected];
            const selectedIndex = selected.indexOf(name);
            if (selectedIndex !== -1) {
                tmpSelected.splice(selectedIndex, 1);
                if (onSelectionChanged) {
                    onSelectionChanged(selected, tmpSelected);
                }

                setSelected(tmpSelected);
            }
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterByName = (event) => {
        setFilterName(event.target.value);
        setPage(0);
    };

    const filteredData = dragEnabled
        ? applyFilterOnly(items, headers, filterName)
        : applySortFilter(items, headers, getComparator(order, orderBy), filterName);
    const displayedData = showPagination
        ? filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : filteredData;
    const emptyRows =
        showPagination && page > 0
            ? Math.max(0, (1 + page) * rowsPerPage - items.length)
            : 0;

    const isDataNotFound = filteredData.length === 0;

    const renderRow = (row) => {
        let list = [];
        for (const key in headers) {
            const id = headers[key].id;
            const alignRight = headers[key].alignRight;
            let cell = row[id];
            list.push(
                <TableCell
                    key={key}
                    component="th"
                    scope="row"
                    padding="normal"
                    align={alignRight ? "right" : "left"}
                >
                    <Stack direction="row" alignItems="center" spacing={2}>
                        {typeof cell === "object" ? (
                            cell
                        ) : (
                            <Typography variant="subtitle2" noWrap>
                                {cell}
                            </Typography>
                        )}
                    </Stack>
                </TableCell>
            );
        }
        return list;
    };

    const handleDragEnd = (event) => {
        if (!dragEnabled || isDragDisabled) {
            return;
        }
        const { active, over } = event;
        if (!active || !over || active.id === over.id) {
            return;
        }
        const activeRow = filteredData.find((row) => row.__dragId === active.id);
        const overRow = filteredData.find((row) => row.__dragId === over.id);
        if (!activeRow || !overRow || activeRow.index === overRow.index) {
            return;
        }
        const reorderedRows = arrayMove(rows, activeRow.index, overRow.index);
        rowDrag.onReorder(reorderedRows, {
            oldIndex: activeRow.index,
            newIndex: overRow.index,
        });
    };

    const renderTableBodyRows = () =>
        displayedData.map((row) => {
            const id = headers[0].id;
            const isItemSelected = selected.indexOf(row[id]) !== -1;
            if (dragEnabled) {
                return (
                    <SortableTableRow
                        key={row.__dragId}
                        row={row}
                        isItemSelected={isItemSelected}
                        renderRow={renderRow}
                        onSelectionChange={(event) => handleClick(event, row[id])}
                        onMouseEnter={() => {
                            setCurrentHoverId(row.index);
                        }}
                        dragId={row.__dragId}
                        dragDisabled={isDragDisabled}
                        dragAriaLabel={t("buttonCommons.reorder", {
                            defaultValue: "Reorder",
                        })}
                    />
                );
            }
            return (
                <TableRow
                    hover
                    key={row.index}
                    tabIndex={-1}
                    role="checkbox"
                    selected={isItemSelected}
                    aria-checked={isItemSelected}
                    onMouseEnter={() => {
                        setCurrentHoverId(row.index);
                    }}
                >
                    <TableCell padding="checkbox">
                        <Checkbox
                            checked={isItemSelected}
                            onChange={(event) => handleClick(event, row[id])}
                        />
                    </TableCell>
                    {renderRow(row)}
                </TableRow>
            );
        });

    return (
        <Card sx={{ width: "100%" }}>
            <SearchList
                numSelected={selected.length}
                filterName={filterName}
                placeholder={searchPlaceholder}
                onFilterName={handleFilterByName}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row-reverse",
                    }}
                >
                    {showChildrenWhenSelected
                        ? showChildrenWhenSelected &&
                          selected.length > 0 &&
                          children
                        : children}
                    {/* {children} */}
                </Box>
            </SearchList>
            {progress && progress.show && (
                <Box sx={{ width: "100%" }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress.value}
                    />
                </Box>
            )}

            <Scrollbar>
                <div
                    onContextMenu={
                        contextMenuOptions ? handleContextMenu : null
                    }
                    style={
                        contextMenuOptions ? { cursor: "context-menu" } : null
                    }
                >
                    <TableContainer sx={{ minWidth: 600 }}>
                        <Table>
                            <ListHead
                                order={order}
                                orderBy={orderBy}
                                headLabel={effectiveHeaders}
                                rowCount={items.length}
                                numSelected={selected.length}
                                onRequestSort={handleRequestSort}
                                onSelectAllClick={handleSelectAllClick}
                            />
                            {dragEnabled ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={displayedData.map((row) => row.__dragId)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <TableBody>
                                            {renderTableBodyRows()}
                                            {emptyRows > 0 && (
                                                <TableRow style={{ height: 33 * emptyRows }}>
                                                    <TableCell colSpan={dragTableColumnSpan} />
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </SortableContext>
                                </DndContext>
                            ) : (
                                <TableBody>
                                    {renderTableBodyRows()}
                                    {emptyRows > 0 && (
                                        <TableRow style={{ height: 33 * emptyRows }}>
                                            <TableCell colSpan={tableColumnSpan} />
                                        </TableRow>
                                    )}
                                </TableBody>
                            )}
                            {isDataNotFound && (
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            align="center"
                                            colSpan={
                                                dragEnabled
                                                    ? dragTableColumnSpan
                                                    : tableColumnSpan
                                            }
                                            sx={{ py: 3 }}
                                        >
                                            <SearchNotFound
                                                searchQuery={filterName}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </div>
            </Scrollbar>

            {showPagination && (
                <TablePagination
                    labelRowsPerPage={t("AdminTable.rowsPerPage")}
                    rowsPerPageOptions={ROWS_PER_PAGE}
                    component="div"
                    count={items.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            )}
            <Menu
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                {contextMenuOptions &&
                    contextMenuOptions.map((option, index) => (
                        <MenuItem
                            key={index}
                            onClick={() => {
                                const hoveredItem = items.find(
                                    (item) => item.index === currentHoverId
                                );
                                if (hoveredItem) {
                                    option.handler(hoveredItem.index);
                                }
                                handleCloseContextMenu();
                            }}
                        >
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.label} />
                        </MenuItem>
                    ))}
            </Menu>
        </Card>
    );
};

AdminTable.propTypes = {
    headers: PropTypes.array,
    rows: PropTypes.array,
    searchPlaceholder: PropTypes.string,
    children: PropTypes.node,
    selection: PropTypes.object.isRequired,
    progress: PropTypes.object,
    rowDrag: PropTypes.shape({
        enabled: PropTypes.bool,
        onReorder: PropTypes.func,
        idField: PropTypes.string,
        getRowId: PropTypes.func,
        disableWhenFiltered: PropTypes.bool,
    }),
};

export default AdminTable;
